import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import dayjs from "shared/dayjs";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import FormControlLabel from "@mui/material/FormControlLabel";
import { AppDispatch, RootState } from "app/store";
import { logoutAuth, setTermsAcceptanceRequired } from "features/Core/Authentification/state/authentificationSlice";
import { AcceptTerms, GetCurrentTerms } from "../state/terms-slice";
import {
    defaultDocumentLanguage,
    PRIVACY_POLICY_URL,
    TERMS_LANGUAGES,
    TERMS_PDF_URL,
    TermsLanguage,
} from "../terms-documents";

const TITLE_ID = 'terms-gate-title';

/**
 * How close to the bottom counts as "read to the end". Taken from the mockup. A tolerance is
 * required, not optional: sub-pixel layout and zoom levels mean scrollTop + clientHeight
 * frequently lands a fraction short of scrollHeight even when the user is at the very bottom.
 */
const SCROLL_END_TOLERANCE_PX = 24;

/**
 * Blocking Terms and Conditions acceptance.
 *
 * Deliberately NOT dismissible: no close button, no backdrop dismiss (MUI only closes via
 * onClose, which is not supplied), and disableEscapeKeyDown. The only way out is checking the
 * box and confirming.
 *
 * Because it cannot be dismissed, every failure mode has to leave the user a way forward —
 * a modal that blocks the whole app and offers nothing is worse than no gate at all. Hence
 * the retry on load failure, the retry on accept failure, and the short-content handling
 * below.
 */
export default function TermsGateModal() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const hasLoginData = useSelector((state: RootState) => state.authentificationSlice.hasLoginData);
    const termsAcceptanceRequired = useSelector((state: RootState) => state.authentificationSlice.termsAcceptanceRequired);
    const dateFormat = useSelector((state: RootState) => state.authentificationSlice.userSettings?.dateFormat);
    const { current, isLoading, isAccepting, loadError, acceptError } =
        useSelector((state: RootState) => state.termsSlice);

    const scrollRef = useRef<HTMLDivElement>(null);
    const [hasReachedEnd, setHasReachedEnd] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<TermsLanguage | null>(null);

    const isOpen = hasLoginData && termsAcceptanceRequired;

    useEffect(() => {
        if (isOpen && current === null && !isLoading && loadError === '') {
            dispatch(GetCurrentTerms());
        }
    }, [isOpen, current, isLoading, loadError, dispatch]);

    // A new version means reading again from the top, and choosing the language again.
    useEffect(() => {
        setHasReachedEnd(false);
        setIsChecked(false);
        setSelectedLanguage(null);
    }, [current?.version]);

    const availableLanguages = useMemo(
        () => TERMS_LANGUAGES.filter(language => Boolean(current?.bodies?.[language])),
        [current]);

    /**
     * Which document the reader sees. Their own choice once they have made one; until then the
     * language they run ZEITON in if the terms ship in it, and otherwise whatever is on offer.
     *
     * Derived rather than held in an effect so it cannot go stale: if the response changes under
     * it — a reload, a new version — a selection that is no longer available falls back to the
     * default in the same render instead of leaving the body blank for a frame.
     */
    const activeLanguage: TermsLanguage | null = useMemo(() => {
        if (selectedLanguage !== null && availableLanguages.includes(selectedLanguage)) {
            return selectedLanguage;
        }

        const appDefault: TermsLanguage = defaultDocumentLanguage(i18n.language);

        return availableLanguages.includes(appDefault) ? appDefault : (availableLanguages[0] ?? null);
    }, [selectedLanguage, availableLanguages, i18n.language]);

    /**
     * Latches once true: scrolling back up must not withdraw permission the user already
     * earned, and re-checking on every scroll event would otherwise flicker the checkbox.
     *
     * It also latches ACROSS languages, by design — reading one of the two versions to the end
     * is enough. They say the same thing, and each is independently binding, so requiring both
     * would be asking the reader to read the same document twice.
     */
    const evaluateScrollPosition = useCallback(() => {
        const element = scrollRef.current;
        if (element === null) {
            return;
        }

        const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
        if (distanceFromBottom <= SCROLL_END_TOLERANCE_PX) {
            setHasReachedEnd(true);
        }
    }, []);

    /**
     * THE edge case: terms shorter than the modal's scroll area never scroll, so a scroll
     * event never fires and the checkbox would stay disabled forever — locking the user out
     * of the whole app with no way forward. Anyone on a large monitor, or with a short terms
     * version, hits this.
     *
     * Evaluating on mount covers it (distanceFromBottom is 0 when nothing overflows), and a
     * ResizeObserver re-evaluates when the container or its content changes size — late web
     * font loading and window resizes both change these numbers after first paint.
     *
     * Keyed on the language too: switching swaps the whole body out, so the observer has to be
     * pointed at the new children.
     */
    useEffect(() => {
        const element = scrollRef.current;
        if (element === null || activeLanguage === null) {
            return;
        }

        evaluateScrollPosition();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => evaluateScrollPosition());
        observer.observe(element);
        // Every child, not just the first: the scroll area holds the terms body *and* the
        // end-of-document line, and either one reflowing changes how far there is left to
        // scroll without changing the container's own size.
        Array.from(element.children).forEach(child => observer.observe(child));

        return () => observer.disconnect();
    }, [activeLanguage, evaluateScrollPosition]);

    /**
     * Switching language starts the new document at its top. Without this the reader lands
     * mid-text at whatever offset the previous language happened to be scrolled to, which reads
     * as the modal having lost its place.
     *
     * `hasReachedEnd` is deliberately left alone — see evaluateScrollPosition above.
     */
    useEffect(() => {
        if (scrollRef.current !== null) {
            scrollRef.current.scrollTop = 0;
        }
    }, [activeLanguage]);

    /**
     * Every link in the terms body opens in a new tab. This modal is undismissable, so a link
     * that navigated in place would take the user out of the gated application entirely — the
     * shipped text links to the privacy policy, which is exactly that case.
     *
     * Done here rather than by writing `target="_blank"` into the content files, because
     * DOMPurify strips `target` (it is not in its default attribute allowlist) and relaxing the
     * sanitiser to let it through would also let authored markup choose `_self`. Forcing it on
     * the rendered nodes makes the guarantee the component's, not something each new terms
     * version has to remember.
     */
    useEffect(() => {
        const element = scrollRef.current;
        if (element === null) {
            return;
        }

        element.querySelectorAll('.terms-modal-text a[href]').forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });
    }, [activeLanguage, current]);

    const confirm = async () => {
        if (current === null) {
            return;
        }

        const result = await dispatch(AcceptTerms(current.version));

        // Only release the gate once the write actually succeeded. On failure the slice sets
        // acceptError and the modal stays put with the confirm button live for another try.
        if (AcceptTerms.fulfilled.match(result)) {
            dispatch(setTermsAcceptanceRequired(false));
        }
    };

    // The one way out for someone who won't accept. Mirrors the app's canonical logout
    // (nav-menu.tsx's handleLogout): a hard redirect rather than client-side routing, so the
    // whole Redux tree — including this modal's own isOpen — resets on the next load instead
    // of racing this component's unmount.
    const handleLogout = () => {
        dispatch(logoutAuth());
        window.location.replace('/login');
    };

    if (!isOpen) {
        return null;
    }

    const canCheck = hasReachedEnd && current !== null && activeLanguage !== null;
    const canConfirm = canCheck && isChecked && !isAccepting;

    // Formatted here rather than through the shared `formatDate` helper, which runs the value
    // through `.tz(getUserTimeZone())`. An effective date is a plain calendar date, not an
    // instant: converting it shifts midnight to the previous day for negative-offset users,
    // so the terms would claim to take effect a day before they do.
    const effectiveDateLabel = current === null
        ? ''
        : dayjs(current.effectiveDate).format(dateFormat || 'DD.MM.YYYY');

    // The documents linked from the checkbox follow the switch, not the application language: the
    // reader takes away a copy of the version they actually read and are accepting.
    //
    // `activeLanguage` is null only when the response carries no body at all — there is nothing to
    // read, so there is no choice to follow; English is the fallback everything else in this
    // feature terminates on.
    const linkLanguage: TermsLanguage = activeLanguage ?? 'en';

    return (
        <Dialog
            open={isOpen}
            disableEscapeKeyDown
            scroll={'paper'}
            aria-labelledby={TITLE_ID}
            className='terms-modal terms-scope'>

            <div className="terms-modal-header">
                {/* Top-right corner, mirroring the What's New modal's close button: the corner
                    is where this app puts "leave this dialog", and keeping it out of the footer
                    stops it reading as an alternative to Confirm. */}
                <Button
                    id="terms-logout-btn"
                    className="btn-modal terms-modal-logout"
                    onClick={handleLogout}>
                    {t('Logout')}
                </Button>

                {/* Icon tile + eyebrow side by side, as in the design. The tile is decorative —
                    it gives the dialog the weight of a document to read rather than the look
                    of an error. The version rides in the eyebrow so the user can see which
                    revision they are being asked to accept without scrolling to the end. */}
                <div className="terms-modal-eyebrow-row">
                    <span className="terms-modal-icon" aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM13 2v7h7M9 13h6M9 17h6" />
                        </svg>
                    </span>
                    <span className="terms-modal-eyebrow">
                        {t('terms.actionRequired')}
                        {current !== null && <> · {current.version}</>}
                    </span>
                </div>

                <h2 id={TITLE_ID} className="terms-modal-title">{t('terms.title')}</h2>
                <p className="terms-modal-intro">{t('terms.modalIntro')}</p>

                {/* Which of the equally binding versions to read. Hidden when only one is on
                    offer, where it would be a control with nothing to choose between. */}
                {availableLanguages.length > 1 && (
                    <div className="terms-modal-langs" role="group" aria-label={t('terms.languageSwitch')!}>
                        {availableLanguages.map(language => (
                            <Button
                                key={language}
                                id={`terms-lang-${language}-btn`}
                                className={`btn-modal terms-modal-lang${language === activeLanguage ? ' is-active' : ''}`}
                                aria-pressed={language === activeLanguage}
                                onClick={() => setSelectedLanguage(language)}>
                                {language.toUpperCase()}
                            </Button>
                        ))}
                    </div>
                )}
            </div>

            {/* Scrollable region. The acceptance controls live OUTSIDE it, per the spec, so
                they stay visible while the user reads. */}
            <div
                ref={scrollRef}
                className="terms-modal-body"
                onScroll={evaluateScrollPosition}
                tabIndex={0}>

                {isLoading && (
                    <div className="terms-modal-state"><CircularProgress size={28} /></div>
                )}

                {!isLoading && loadError !== '' && (
                    <div className="terms-modal-state">
                        <p>{t('terms.loadError')}</p>
                        <Button id="terms-retry-btn" className="btn-modal"
                            onClick={() => dispatch(GetCurrentTerms())}>
                            {t('terms.retry')}
                        </Button>
                    </div>
                )}

                {!isLoading && loadError === '' && current !== null && activeLanguage !== null && (
                    <>
                        {/* Repo-authored content served by our own API, but sanitised anyway:
                            defence in depth costs nothing here, and the app already uses
                            DOMPurify for every other HTML body it renders. */}
                        <div
                            className="terms-modal-text"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(current.bodies[activeLanguage]) }} />

                        {/* Chrome, not content — it is built from the response's own metadata,
                            so it stays correct whatever the authored body says. It earns its
                            place by making the end of the document *visible*: the scroll gate
                            is otherwise an invisible rule the user can only discover by
                            obeying it. */}
                        <p className="terms-modal-end">
                            {t('terms.documentEnd', {
                                version: current.version,
                                date: effectiveDateLabel,
                            })}
                        </p>
                    </>
                )}

                {!isLoading && loadError === '' && (current === null || activeLanguage === null) && (
                    <div className="terms-modal-state">{t('terms.unavailable')}</div>
                )}
            </div>

            <div className="terms-modal-footer">
                {!hasReachedEnd && current !== null && (
                    <p className="terms-modal-hint">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 5v14M6 13l6 6 6-6" />
                        </svg>
                        {t('terms.scrollHint')}
                    </p>
                )}

                {acceptError !== '' && (
                    <p className="terms-modal-error">{t('terms.acceptError')}</p>
                )}

                {/* Checkbox left, confirm right — the two halves of the one gate, on one line.
                    Logout is not here: it sits in the header's top-right corner, so this row is
                    only the accept path. */}
                <div className="terms-modal-actions">
                    <FormControlLabel
                        className="terms-modal-accept"
                        control={
                            <Checkbox
                                id="terms-accept-checkbox"
                                checked={isChecked}
                                disabled={!canCheck}
                                onChange={(event) => setIsChecked(event.target.checked)} />
                        }
                        label={
                            <span className="terms-modal-accept-text">
                                {/* Trans, not composed t() calls: the label is one sentence with
                                    two links inside it, and the pieces around them do not survive
                                    translation as separate strings — German puts the verb at the
                                    end. Translators get the whole sentence and place the tags. */}
                                <Trans
                                    t={t}
                                    i18nKey="terms.acceptLabel"
                                    components={{
                                        // These anchors are templates: Trans clones each one and
                                        // fills it with the text between the matching tags in the
                                        // translation, so they are never rendered childless — which
                                        // is what jsx-a11y/anchor-has-content cannot see here.
                                        //
                                        // stopPropagation because these anchors sit inside the
                                        // FormControlLabel's <label>. The HTML spec already
                                        // exempts interactive descendants from a label's
                                        // activation behaviour, so this is a guard rather than
                                        // the fix for a reproduced bug — but "opening the terms
                                        // silently accepted them" is the wrong thing to leave
                                        // resting on that.
                                        termsLink: (
                                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                                            <a
                                                href={TERMS_PDF_URL[linkLanguage]}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(event) => event.stopPropagation()} />
                                        ),
                                        privacyLink: (
                                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                                            <a
                                                href={PRIVACY_POLICY_URL[linkLanguage]}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(event) => event.stopPropagation()} />
                                        ),
                                    }} />
                            </span>
                        } />

                    <Button
                        id="terms-confirm-btn"
                        type="submit"
                        className="btn-modal terms-modal-confirm"
                        disabled={!canConfirm}
                        onClick={confirm}>
                        {isAccepting ? <CircularProgress size={18} /> : t('terms.confirm')}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
